import { useEffect, useRef, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

interface ModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  testId: string;
  wide?: boolean;
  trapFocus?: boolean;
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Keeps Tab focus inside the dialog and returns focus to the opener on close.
function useFocusTrap(ref: RefObject<HTMLDivElement | null>, active: boolean) {
  useEffect(() => {
    if (!active) return;
    const opener = document.activeElement as HTMLElement | null;
    const items = () => Array.from(ref.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
    items()[0]?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const list = items();
      if (!list.length) return;
      const [first, last] = [list[0], list[list.length - 1]];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); opener?.focus(); };
  }, [ref, active]);
}

function useEscape(open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
}

export function Drawer({ open, title, onClose, children, testId }: ModalProps) {
  useEscape(open, onClose);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label={title}>
      <div className="absolute inset-0 animate-fade bg-black/55 backdrop-blur-[3px]" onClick={onClose} />
      <aside data-testid={testId} className="glass-overlay relative h-full w-full max-w-xl animate-slide-in overflow-y-auto rounded-l-[22px] border-r-0 p-8">
        <div className="mb-6 flex items-start justify-between gap-4">
          <h2 className="text-2xl font-medium tracking-[-0.015em] text-white">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close" data-testid={`${testId}-close-button`} className="rounded-full p-1.5 text-zinc-400 transition-colors hover:bg-white/10 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </aside>
    </div>,
    document.body,
  );
}

export function Modal({ open, title, onClose, children, testId, wide, trapFocus }: ModalProps) {
  useEscape(open, onClose);
  const box = useRef<HTMLDivElement>(null);
  useFocusTrap(box, open && !!trapFocus);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={title}>
      <div className="absolute inset-0 animate-fade bg-black/60 backdrop-blur-[3px]" onClick={onClose} />
      <div ref={box} data-testid={testId} className={`glass-overlay relative max-h-[92vh] w-full ${wide ? "max-w-3xl" : "max-w-lg"} animate-pop overflow-y-auto rounded-[22px] p-8`}>
        <div className="mb-6 flex items-start justify-between gap-4">
          <h2 className="text-2xl font-medium tracking-[-0.015em] text-white">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close" data-testid={`${testId}-close-button`} className="rounded-full p-1.5 text-zinc-400 transition-colors hover:bg-white/10 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

export interface TabDef {
  key: string;
  label: string;
  count?: number;
}

export function Tabs({ tabs, active, onChange }: { tabs: TabDef[]; active: string; onChange: (k: string) => void }) {
  return (
    <div role="tablist" className="glass mb-8 inline-flex max-w-full gap-1 overflow-x-auto rounded-full p-1">
      {tabs.map((t) => (
        <button
          key={t.key}
          role="tab"
          type="button"
          aria-selected={active === t.key}
          onClick={() => onChange(t.key)}
          data-testid={`tab-${t.key}`}
          className={`flex items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-[background-color,box-shadow,color,border-color] duration-200 ${
            active === t.key ? "border-signal/70 bg-[rgba(255,29,0,0.14)] text-white shadow-[0_0_16px_-4px_rgba(255,29,0,0.6)]" : "border-transparent text-zinc-400 hover:bg-white/[0.06] hover:text-white"
          }`}
        >
          {t.label}
          {t.count !== undefined && (
            <span data-testid={`tab-${t.key}-count`} className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${t.count > 0 ? "bg-[rgba(255,29,0,0.28)] text-white" : "bg-white/10 text-zinc-300"}`}>
              {t.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

export function SelectFilter({ value, onChange, options, testId, label }: {
  value: string; onChange: (v: string) => void; options: [string, string][]; testId: string; label: string;
}) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="h-11 rounded-[12px] border border-white/15 bg-surface px-3 text-sm text-white focus:border-signal focus:outline-none"
    >
      {options.map(([v, l]) => (
        <option key={v} value={v}>{l}</option>
      ))}
    </select>
  );
}
